import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin, Loader2, Search, X } from 'lucide-react';

const containerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '12px'
};

// Default center - Dubai, UAE
const defaultCenter = {
  lat: 25.2048,
  lng: 55.2708
};

// Free geocoding via OpenStreetMap Nominatim (no billing required)
const NOMINATIM = 'https://nominatim.openstreetmap.org';

const searchPlaces = async (query, lang = 'ar') => {
  const url = `${NOMINATIM}/search?q=${encodeURIComponent(query)}&countrycodes=ae&format=json&limit=6&addressdetails=0&accept-language=${lang}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  return res.json();
};

const reverseGeocode = async (lat, lng, lang = 'ar') => {
  try {
    const url = `${NOMINATIM}/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=${lang}`;
    const res = await fetch(url);
    if (!res.ok) return '';
    const data = await res.json();
    return data.display_name || '';
  } catch {
    return '';
  }
};

const GoogleMapPicker = ({ 
  onLocationSelect, 
  initialLocation = null,
  isDarkMode = false,
  language = 'ar',
  readOnly = false // For display-only mode (customer app)
}) => {
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [address, setAddress] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [map, setMap] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  // Set initial location if provided
  useEffect(() => {
    if (initialLocation && initialLocation.lat && initialLocation.lng) {
      setSelectedLocation(initialLocation);
      reverseGeocode(initialLocation.lat, initialLocation.lng, language).then(addr => {
        if (addr) setAddress(addr);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = useCallback((event) => {
    if (readOnly) return;
    
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const newLocation = { lat, lng };
    
    setSelectedLocation(newLocation);
    
    // Notify parent immediately with coordinates (don't depend on geocoder success)
    if (onLocationSelect) {
      onLocationSelect({ lat, lng, address: '' });
    }
    
    // Resolve address (best-effort, free service)
    reverseGeocode(lat, lng, language).then(addr => {
      if (addr) {
        setAddress(addr);
        if (onLocationSelect) {
          onLocationSelect({ lat, lng, address: addr });
        }
      }
    });
  }, [onLocationSelect, readOnly, language]);

  // Live suggestions while typing (min 2 chars, debounced)
  const handleQueryChange = (value) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchPlaces(value.trim(), language);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
  };

  const selectSuggestion = (place) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    const newLocation = { lat, lng };
    
    setSelectedLocation(newLocation);
    setAddress(place.display_name);
    setSearchQuery(place.display_name.split(',')[0]);
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (map) {
      map.panTo(newLocation);
      map.setZoom(15);
    }
    
    if (onLocationSelect) {
      onLocationSelect({ lat, lng, address: place.display_name });
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await searchPlaces(searchQuery.trim(), language);
      if (results.length > 0) {
        selectSuggestion(results[0]);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } finally {
      setIsSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, map, onLocationSelect, language]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        selectSuggestion(suggestions[0]);
      } else {
        handleSearch();
      }
    }
  };

  const clearLocation = () => {
    setSelectedLocation(null);
    setAddress('');
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onLocationSelect) {
      onLocationSelect(null);
    }
  };

  // Dark mode map styles
  const darkModeStyles = [
    { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
    { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#0e1626" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#255763" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#283d6a" }] },
  ];

  if (loadError) {
    return (
      <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
        <p>{language === 'ar' ? 'خطأ في تحميل الخريطة' : 'Error loading map'}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`p-8 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
        <Loader2 className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-[#1E9BD9]' : 'text-blue-500'}`} />
        <span className={`ms-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {language === 'ar' ? 'جاري تحميل الخريطة...' : 'Loading map...'}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3" ref={containerRef}>
      {/* Search Box with live suggestions - Only show if not read-only */}
      {!readOnly && (
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleQueryChange(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder={language === 'ar' ? 'اكتب اسم المنطقة... (مثال: رأس الخيمة)' : 'Type area name... (e.g., Ras Al Khaimah)'}
                className={`w-full px-4 py-2 pe-10 border rounded-xl ${
                  isDarkMode 
                    ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-500' 
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
                data-testid="map-search-input"
              />
              {isSearching ? (
                <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
              ) : searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSuggestions([]); setShowSuggestions(false); }}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-4 py-2 bg-[#1E9BD9] text-white rounded-xl hover:bg-[#1E9BD9]/90 disabled:opacity-50 flex items-center gap-2"
              data-testid="map-search-button"
            >
              <Search className="h-4 w-4" />
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div 
              className={`absolute top-full mt-1 start-0 end-0 z-50 rounded-xl border shadow-2xl overflow-hidden ${
                isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'
              }`}
              data-testid="map-suggestions-list"
            >
              {suggestions.map((place, idx) => (
                <button
                  key={place.place_id || idx}
                  onClick={() => selectSuggestion(place)}
                  className={`w-full text-start px-4 py-2.5 flex items-start gap-2 transition-colors ${
                    isDarkMode 
                      ? 'hover:bg-white/10 text-gray-200 border-b border-white/5' 
                      : 'hover:bg-gray-50 text-gray-700 border-b border-gray-100'
                  } last:border-b-0`}
                  data-testid={`map-suggestion-${idx}`}
                >
                  <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-blue-500'}`} />
                  <span className="text-sm line-clamp-2">{place.display_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={selectedLocation || defaultCenter}
          zoom={selectedLocation ? 15 : 12}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            styles: isDarkMode ? darkModeStyles : [],
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: readOnly ? 'cooperative' : 'greedy'
          }}
        >
          {selectedLocation && (
            <Marker
              position={selectedLocation}
              animation={window.google?.maps?.Animation?.DROP}
            />
          )}
        </GoogleMap>

        {/* Click instruction overlay - Only show if not read-only and no location selected */}
        {!readOnly && !selectedLocation && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {language === 'ar' ? 'انقر على الخريطة لتحديد الموقع' : 'Click on map to select location'}
          </div>
        )}
      </div>

      {/* Selected Location Display */}
      {selectedLocation && (
        <div className={`p-3 rounded-xl flex items-start gap-3 ${
          isDarkMode ? 'bg-[#0F172A] border border-white/10' : 'bg-gray-50 border border-gray-200'
        }`}>
          <MapPin className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-blue-500'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {language === 'ar' ? 'الموقع المحدد' : 'Selected Location'}
            </p>
            <p className={`text-xs mt-1 break-words ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {address || `${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`}
            </p>
          </div>
          {!readOnly && (
            <button
              onClick={clearLocation}
              className="text-red-500 hover:text-red-600 p-1"
              data-testid="map-clear-location"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleMapPicker;
